/*
 * Copyright © 2018 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschützt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulässig. Dies gilt
 * insbesondere für Vervielfältigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht übergeben, ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 */

import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {interval, merge, MonoTypeOperatorFunction, Observable} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {LogFactory} from '../../core/logging/log-factory';
import {ERROR, ERRORS} from './errors';

const LOG = LogFactory.createLog('ErrorComponent');

const formValidationDelay = 250;

/**
 * Temporary async validator fix until
 * https://github.com/angular/angular/issues/13200 and
 * https://github.com/angular/angular/issues/14542 are addressed.
 *
 * Takes an optional interval as the second parameter which is the period
 * in milliseconds at which the form status will be checked. Default 250ms.
 *
 * src: https://github.com/MatthiasKunnen/angular-form-status-workaround
 */
const fixFormStatus: (
    control: AbstractControl,
    interval?: number,
) => MonoTypeOperatorFunction<string> = (control, i = formValidationDelay) => source$ =>
    merge(
        source$,
        interval(i).pipe(map(() => control.status)),
    ).pipe(
        distinctUntilChanged(),
    );

@Component({
    selector: 'elohn-error',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './error.component.html',
})
export class ErrorComponent implements OnInit {

    @Input() public control: AbstractControl = new UntypedFormControl();

    public readonly ERROR = ERROR;
    public error$?: Observable<ERROR>;

    private readonly errorOrder: Map<ERROR, (key: ERROR) => boolean> = new Map();

    public ngOnInit(): void {
        // The insertion order: only the first error (based on a truthy callback function) is shown
        this.errorOrder.set(ERROR.required, key => this.hasError(key));
        this.errorOrder.set(ERROR.requiredtrue, key => this.hasError(key));
        this.errorOrder.set(ERROR.min, key => this.hasError(key));
        this.errorOrder.set(ERROR.max, key => this.hasError(key));
        this.errorOrder.set(ERROR.email, key => this.hasError(key));
        this.errorOrder.set(ERROR.minLength, key => this.hasError(key));
        this.errorOrder.set(ERROR.maxLength, key => this.hasError(key));
        this.errorOrder.set(ERROR.pattern, key => this.hasError(key));
        this.errorOrder.set(ERROR.dateFormat, key => this.hasError(key));
        this.errorOrder.set(ERROR.isInYear, key => this.hasError(key));
        this.errorOrder.set(ERROR.minDate, key => this.hasError(key));
        this.errorOrder.set(ERROR.maxDate, key => this.hasError(key));
        this.errorOrder.set(ERROR.matDatepickerFilter, key => this.hasError(key));
        this.errorOrder.set(ERROR.phone, key => this.hasError(key));
        this.errorOrder.set(ERROR.ahvNummer, key => this.hasError(key));
        this.errorOrder.set(ERROR.integer, key => this.hasError(key));
        this.errorOrder.set(ERROR.geschlechtAusgewaehlt, key => this.hasError(key));
        this.errorOrder.set(ERROR.invalidXMLZeichen, key => this.hasError(key));
        this.errorOrder.set(ERROR.UNBEKANNT, key => this.hasError(key));

        this.error$ = this.control.statusChanges.pipe(
            fixFormStatus(this.control),
            map(() => this.getError()),
        );
    }

    private hasError(errorKey: ERROR): boolean {
        if (ERRORS[errorKey].some(key => this.control.hasError(key))) {
            return true;
        }
        // return this.control.hasError(errorKey) does not work on async validation?
        const type = typeof ((this.control.errors || {}).value || {})[errorKey];

        return type !== 'undefined';
    }

    public getError(): ERROR {
        if (this.errorOrder.size === 0) {
            return ERROR.NONE;
        }

        const errorEntry = Array.from(this.errorOrder)
            .find(([key, hasError]) => hasError(key));

        if (errorEntry) {
            return errorEntry[0];
        }

        if (this.control.invalid) {
            const path = tryBuildControlPath(this.control);
            LOG.error('unhandled error', path, this.control.errors, this.control);

            return ERROR.UNBEKANNT;
        }

        return ERROR.NONE;
    }
}

function tryBuildControlPath(control: AbstractControl): string {
    if (control.parent instanceof UntypedFormArray) {
        return '<FormArray>.<FormControl>';
    }

    const parent = control.parent;
    // noinspection SuspiciousTypeOfGuard
    if (parent instanceof UntypedFormGroup) {
        for (const child in parent.controls) {
            if (parent.controls[child] === control) {
                return `<FormGroup>.${child}`;
            }
        }
    }

    return '<Unknown>.<FormControl>';

}
